import path from 'node:path';
import process from 'node:process';
import opentelemetry, { type Tracer, type Meter } from '@opentelemetry/api';
import logfmt from 'logfmt';
import winston from 'winston';

export type Logger = winston.Logger & {
  tracer: Tracer;
  metrics: Meter;
  getActiveSpan: typeof opentelemetry.trace.getActiveSpan;
};

const formatMessage = (info: winston.Logform.TransformableInfo) =>
  `level=${info.level} ${
    typeof info.message === 'string'
      ? `msg="${info.message?.trim()}"`
      : logfmt.stringify(info.message)
  } ${logfmt.stringify(info.metadata).replace(/\n/gm, '\\n\\t')}`;

const logger = winston.createLogger({
  level: process.env.LOG_LEVEL || 'debug',
  format: winston.format.json(),
  levels: winston.config.syslog.levels,
  defaultMeta: {
    service_name: process.env.SERVICE_NAME || 'unknown',
  },
  transports: [
    new winston.transports.Console({
      format: winston.format.combine(
        winston.format.metadata(),
        winston.format.printf(formatMessage),
      ),
    }),
  ],
}) as Logger;

logger.warn = logger.warning;
logger.verbose = logger.debug;
logger.http = logger.debug;

export default (filename: string, metaData: Record<string, string> = {}): Logger => {
  const relPath = filename
    .replace(`${path.dirname(process.cwd())}/`, '')
    // workaround for containerized services
    .replace('/app/services/', '')
    .replace(/\.ts|\.js|\.tsx|\.jsx/, '')
    .split('/');

  const child = logger.child({ module: relPath.join('::'), ...metaData });
  // we create tracer and metrics only, if not in browser
  child.tracer = opentelemetry.trace.getTracer(relPath.join('::'));
  child.getActiveSpan = opentelemetry.trace.getActiveSpan;
  child.metrics = opentelemetry.metrics.getMeter(relPath.join('::'));
  return child;
};

export const getRequestLoggerMiddleware = (logger: winston.Logger, module: string) => {
  logfmt.stream = {
    write: (message: string) => {
      try {
        const msgObj = logfmt.parse(message);
        logger.http('REQUEST', { module, ...msgObj });
      } catch (error) {
        logger.error(error);
      }
    },
  };

  const middleware = logfmt.requestLogger();

  return middleware;
};

export const withNewSpan = <F extends CallableFunction>(
  spanName: string,
  logger: Logger,
  fn: F,
): F => {
  return ((...args: Array<any>) => {
    return logger.tracer.startActiveSpan(spanName, (span) => {
      try {
        return fn(...args);
      } finally {
        span.end();
      }
    });
  }) as unknown as F;
};

export function withNewSpanDec(spanName: string, logger: Logger) {
  return (target: any, _context: ClassMethodDecoratorContext<any>) => {
    return function (this: any, ...args: Array<any>) {
      return logger.tracer.startActiveSpan(spanName, async (span) => {
        try {
          const result = await target.apply(this, args);
          return result;
        } finally {
          span.end();
        }
      });
    };
  };
}
